import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Stack,
  Title,
  Stepper,
  Button,
  Group,
  TextInput,
  Textarea,
  Select,
  Switch,
  Paper,
  Text,
  Badge,
  Alert,
  Progress,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconArrowLeft, IconArrowRight, IconSend, IconAlertTriangle } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import { useAuthStore, useUserTenants } from '@/store/auth'
import { routinesApi, type RoutineBackend, type RoutineCreateBody } from '@/api/routines'

// ─── Tipos de Recurso ────────────────────────────────────────────────────────

type ResourceType = 'rds' | 'ec2'

function backendFromResource(type: ResourceType): RoutineBackend {
  return type === 'rds' ? 'sql' : 'shell'
}

// ─── Timezones disponíveis ──────────────────────────────────────────────────

const TIMEZONES = [
  { value: 'America/Manaus', label: 'America/Manaus (UTC-4)' },
  { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (UTC-3)' },
  { value: 'America/Belem', label: 'America/Belem (UTC-3)' },
  { value: 'America/Fortaleza', label: 'America/Fortaleza (UTC-3)' },
  { value: 'America/Recife', label: 'America/Recife (UTC-3)' },
  { value: 'America/Noronha', label: 'America/Noronha (UTC-2)' },
  { value: 'UTC', label: 'UTC' },
]

// ─── Validação ───────────────────────────────────────────────────────────────

/**
 * Valida SQL: não pode ser vazio e não pode ter ponto-e-vírgula no meio
 * (apenas statement único é permitido).
 * Retorna mensagem de erro ou null se válido.
 */
function validateSql(sql: string): string | null {
  const trimmed = sql.trim()
  if (!trimmed) return 'O SQL não pode ser vazio.'
  // Remove ponto-e-vírgula final (permitido), verifica se há outros no meio
  const withoutTrailingSemicolon = trimmed.replace(/;$/, '')
  if (withoutTrailingSemicolon.includes(';')) {
    return 'Apenas um statement SQL é permitido (sem ";" no meio da query).'
  }
  return null
}

function validateScript(script: string): string | null {
  if (!script.trim()) return 'O script não pode ser vazio.'
  return null
}

/**
 * Valida a expressão de agendamento no formato EventBridge.
 * Aceita `cron(...)` ou `rate(N minute|minutes|hour|hours|day|days)`.
 */
const SCHEDULE_EXPR_RE =
  /^(cron\(.+\)|rate\(\s*\d+\s+(minute|minutes|hour|hours|day|days)\s*\))$/

function validateScheduleExpr(expr: string): string | null {
  const trimmed = expr.trim()
  if (!trimmed) return 'A expressão de agendamento é obrigatória.'
  if (!SCHEDULE_EXPR_RE.test(trimmed)) {
    return 'Formato EventBridge inválido. Use cron(...) ou rate(N minute|hour|day). Ex.: cron(0 3 * * ? *) ou rate(1 day).'
  }
  return null
}

// ─── Componente Principal ────────────────────────────────────────────────────

export function RoutineWizard() {
  const navigate = useNavigate()
  const tenants = useUserTenants()
  const { selectedTenantId } = useAuthStore()
  const [active, setActive] = useState(0)

  // ── Passo 0: Tenant ──
  const [tenantId, setTenantId] = useState<string | null>(
    selectedTenantId != null ? String(selectedTenantId) : null,
  )
  const [tenantError, setTenantError] = useState<string | null>(null)

  // ── Passo 1: Recurso ──
  const [resourceType, setResourceType] = useState<ResourceType>('rds')
  const [name, setName] = useState('')
  const [nameError, setNameError] = useState<string | null>(null)
  const [description, setDescription] = useState('')

  // ── Passo 2: Editor ──
  // SQL
  const [sql, setSql] = useState('')
  const [sqlError, setSqlError] = useState<string | null>(null)
  const [dbIdentifier, setDbIdentifier] = useState('')
  const [dbIdentifierError, setDbIdentifierError] = useState<string | null>(null)
  const [credentialId, setCredentialId] = useState('')
  const [credentialIdError, setCredentialIdError] = useState<string | null>(null)
  // Shell
  const [script, setScript] = useState('')
  const [scriptError, setScriptError] = useState<string | null>(null)
  const [instanceId, setInstanceId] = useState('')
  const [instanceIdError, setInstanceIdError] = useState<string | null>(null)
  const [region, setRegion] = useState('')
  const [regionError, setRegionError] = useState<string | null>(null)

  // ── Passo 3: Agenda ──
  const [scheduleExpr, setScheduleExpr] = useState('')
  const [scheduleError, setScheduleError] = useState<string | null>(null)
  const [timezone, setTimezone] = useState('America/Manaus')
  const [freezeRespect, setFreezeRespect] = useState(true)

  const backend = backendFromResource(resourceType)
  const steps = ['Tenant', 'Recurso', 'Editor', 'Agenda', 'Revisão']

  // ─── Validação por passo ──────────────────────────────────────────────────

  function validateStep(step: number): boolean {
    if (step === 0) {
      if (!tenantId) {
        setTenantError('Selecione um tenant.')
        return false
      }
      setTenantError(null)
      return true
    }

    if (step === 1) {
      if (!name.trim()) {
        setNameError('O nome da rotina é obrigatório.')
        return false
      }
      setNameError(null)
      return true
    }

    if (step === 2) {
      if (backend === 'sql') {
        const sqlErr = validateSql(sql)
        const dbErr = !dbIdentifier.trim() ? 'O identificador do banco é obrigatório.' : null
        const credErr = !credentialId.trim() ? 'O ID da credencial é obrigatório.' : null
        setSqlError(sqlErr)
        setDbIdentifierError(dbErr)
        setCredentialIdError(credErr)
        return !sqlErr && !dbErr && !credErr
      } else {
        const scriptErr = validateScript(script)
        const instErr = !instanceId.trim() ? 'O ID da instância é obrigatório.' : null
        const regErr = !region.trim() ? 'A região é obrigatória.' : null
        setScriptError(scriptErr)
        setInstanceIdError(instErr)
        setRegionError(regErr)
        return !scriptErr && !instErr && !regErr
      }
    }

    if (step === 3) {
      const schedErr = validateScheduleExpr(scheduleExpr)
      setScheduleError(schedErr)
      return !schedErr
    }

    return true
  }

  /**
   * Revalida TODOS os passos 0–3 antes do submit. Como o Stepper não permite
   * mais pular passos via clique, isto é uma dupla-trava: nenhum submit passa
   * sem que cada passo esteja válido. Retorna o primeiro passo inválido (ou -1).
   */
  function firstInvalidStep(): number {
    for (let s = 0; s <= 3; s++) {
      if (!validateStep(s)) return s
    }
    return -1
  }

  // ─── Build payload ───────────────────────────────────────────────────────

  function buildBody(): RoutineCreateBody {
    const target_ref =
      backend === 'sql'
        ? { db_identifier: dbIdentifier.trim(), credential_id: credentialId.trim() }
        : { instance_id: instanceId.trim(), region: region.trim() }

    const payload =
      backend === 'sql'
        ? { sql: sql.trim() }
        : { script: script.trim() }

    return {
      name: name.trim(),
      description: description.trim() || undefined,
      backend,
      target_ref,
      payload,
      schedule_expr: scheduleExpr.trim(),
      timezone,
      freeze_respect: freezeRespect,
    }
  }

  // ─── Submit ──────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: () => routinesApi.createRoutine(Number(tenantId), buildBody()),
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: 'Rotina criada! Aguardando aprovação de outro usuário antes de ativar.',
      })
      navigate('/rotinas')
    },
    onError: (err: Error) => {
      notifications.show({
        color: 'red',
        message: `Erro ao criar rotina: ${err.message}`,
      })
    },
  })

  // ─── Navegação ───────────────────────────────────────────────────────────

  function handleNext() {
    if (validateStep(active)) {
      setActive((a) => Math.min(a + 1, steps.length - 1))
    }
  }

  function handleBack() {
    setActive((a) => Math.max(a - 1, 0))
  }

  function handleSubmit() {
    // Revalida todos os passos 0–3; não confia apenas em validateStep(4).
    const bad = firstInvalidStep()
    if (bad !== -1) {
      // Leva o usuário de volta ao primeiro passo inválido para corrigir.
      setActive(bad)
      return
    }
    createMutation.mutate()
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  const tenantName =
    tenantId != null
      ? (tenants.find((t) => String(t.id) === tenantId)?.name ?? tenantId)
      : '—'

  return (
    <Stack gap="lg" maw={800} mx="auto">
      <Group>
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={14} />}
          onClick={() => navigate('/rotinas')}
          aria-label="Voltar para lista de rotinas"
        >
          Voltar
        </Button>
        <Title order={3}>Nova Rotina Agendada</Title>
      </Group>

      <Progress
        value={(active / (steps.length - 1)) * 100}
        size="xs"
        color="blue"
        role="status"
        aria-label={`Etapa ${active + 1} de ${steps.length}`}
      />

      {/* Sem onStepClick: navegação só via botões Próximo/Anterior, que
          passam pela validação — impede pular passos inválidos. */}
      <Stepper active={active} size="sm">
        {steps.map((label) => (
          <Stepper.Step key={label} label={label} />
        ))}
      </Stepper>

      <Paper shadow="xs" p="lg" withBorder>
        {/* ── Passo 0: Selecionar Tenant ──────────────────────────── */}
        {active === 0 && (
          <Stack gap="md">
            <Text fw={500}>Selecione o tenant onde a rotina será criada</Text>
            <Select
              label="Tenant"
              required
              placeholder="Selecione um tenant"
              data={tenants.map((t) => ({
                value: String(t.id),
                label: `${t.name} (${t.environment})`,
              }))}
              value={tenantId}
              onChange={(v) => {
                setTenantId(v)
                if (tenantError) setTenantError(null)
              }}
              error={tenantError}
              searchable
            />
          </Stack>
        )}

        {/* ── Passo 1: Recurso ────────────────────────────────────── */}
        {active === 1 && (
          <Stack gap="md">
            <TextInput
              label="Nome da Rotina"
              required
              placeholder="Ex.: Limpeza de logs diária"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (nameError) setNameError(null)
              }}
              error={nameError}
            />
            <Textarea
              label="Descrição (opcional)"
              rows={2}
              placeholder="Descreva o objetivo desta rotina..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <Select
              label="Tipo de Recurso"
              required
              data={[
                { value: 'rds', label: 'RDS (banco de dados)' },
                { value: 'ec2', label: 'EC2 (instância)' },
              ]}
              value={resourceType}
              onChange={(v) => setResourceType((v as ResourceType) ?? 'rds')}
            />
            <Group gap="xs" align="center">
              <Text size="sm" c="dimmed">
                Backend derivado automaticamente:
              </Text>
              <Badge color={backend === 'sql' ? 'blue' : 'orange'} variant="light">
                {backend === 'sql' ? 'SQL (RDS)' : 'Shell (EC2)'}
              </Badge>
            </Group>
          </Stack>
        )}

        {/* ── Passo 2: Editor ─────────────────────────────────────── */}
        {active === 2 && backend === 'sql' && (
          <Stack gap="md">
            <Text fw={500}>Configuração SQL</Text>
            <TextInput
              label="Identificador do banco (db_identifier)"
              required
              placeholder="rds-producao-01"
              // TODO: buscar lista de instâncias RDS via inventário quando disponível
              value={dbIdentifier}
              onChange={(e) => {
                setDbIdentifier(e.target.value)
                if (dbIdentifierError) setDbIdentifierError(null)
              }}
              error={dbIdentifierError}
            />
            <TextInput
              label="ID da Credencial (credential_id)"
              required
              placeholder="Ex.: 42"
              // TODO: buscar credenciais via databaseQueryApi.listCredentials quando disponível
              value={credentialId}
              onChange={(e) => {
                setCredentialId(e.target.value)
                if (credentialIdError) setCredentialIdError(null)
              }}
              error={credentialIdError}
            />
            <Textarea
              label="SQL"
              aria-label="SQL"
              required
              rows={8}
              placeholder="SELECT * FROM tabela WHERE condicao = 'valor'"
              description="Apenas um statement SQL (sem ; no meio). O ; final é opcional."
              value={sql}
              onChange={(e) => {
                setSql(e.target.value)
                if (sqlError) setSqlError(null)
              }}
              error={sqlError}
              styles={{ input: { fontFamily: 'monospace' } }}
            />
          </Stack>
        )}

        {active === 2 && backend === 'shell' && (
          <Stack gap="md">
            <Text fw={500}>Configuração Shell</Text>
            <TextInput
              label="ID da Instância EC2 (instance_id)"
              required
              placeholder="i-0abcdef1234567890"
              // TODO: buscar lista de instâncias EC2 via inventário quando disponível
              value={instanceId}
              onChange={(e) => {
                setInstanceId(e.target.value)
                if (instanceIdError) setInstanceIdError(null)
              }}
              error={instanceIdError}
            />
            <TextInput
              label="Região AWS (region)"
              required
              placeholder="sa-east-1"
              value={region}
              onChange={(e) => {
                setRegion(e.target.value)
                if (regionError) setRegionError(null)
              }}
              error={regionError}
            />
            <Textarea
              label="Script Shell"
              required
              rows={10}
              placeholder="#!/bin/bash&#10;echo 'Iniciando rotina...'&#10;# ..."
              value={script}
              onChange={(e) => {
                setScript(e.target.value)
                if (scriptError) setScriptError(null)
              }}
              error={scriptError}
              styles={{ input: { fontFamily: 'monospace' } }}
            />
          </Stack>
        )}

        {/* ── Passo 3: Agenda ─────────────────────────────────────── */}
        {active === 3 && (
          <Stack gap="md">
            <TextInput
              label="Expressão de Agendamento (EventBridge)"
              required
              placeholder="cron(0 3 * * ? *) ou rate(1 day)"
              description="Formato EventBridge: cron(min hora dom mês dow ano) ou rate(N unit)"
              value={scheduleExpr}
              onChange={(e) => {
                setScheduleExpr(e.target.value)
                if (scheduleError) setScheduleError(null)
              }}
              error={scheduleError}
            />
            <Select
              label="Fuso Horário"
              required
              data={TIMEZONES}
              value={timezone}
              onChange={(v) => setTimezone(v ?? 'America/Manaus')}
              searchable
            />
            <Switch
              label="Respeitar janelas de congelamento (freeze_respect)"
              description="Quando ativo, a rotina é pausada automaticamente durante períodos de congelamento configurados."
              checked={freezeRespect}
              onChange={(e) => setFreezeRespect(e.currentTarget.checked)}
            />
          </Stack>
        )}

        {/* ── Passo 4: Revisão ────────────────────────────────────── */}
        {active === 4 && (
          <Stack gap="md">
            <Text fw={500} size="lg">
              Revisão da Rotina
            </Text>

            <Alert
              icon={<IconAlertTriangle size={16} />}
              color="yellow"
              variant="light"
              title="Aprovação obrigatória"
            >
              Esta rotina exige aprovação de outra pessoa antes de ativar. Ela será criada
              com status <strong>pendente</strong> até que um aprovador diferente do
              criador confirme a execução.
            </Alert>

            <Stack gap="xs">
              <Group>
                <Text size="sm" fw={500} w={160}>
                  Tenant:
                </Text>
                <Text size="sm">{tenantName}</Text>
              </Group>
              <Group>
                <Text size="sm" fw={500} w={160}>
                  Nome:
                </Text>
                <Text size="sm">{name}</Text>
              </Group>
              {description && (
                <Group>
                  <Text size="sm" fw={500} w={160}>
                    Descrição:
                  </Text>
                  <Text size="sm">{description}</Text>
                </Group>
              )}
              <Group>
                <Text size="sm" fw={500} w={160}>
                  Tipo de Recurso:
                </Text>
                <Text size="sm">{resourceType === 'rds' ? 'RDS' : 'EC2'}</Text>
              </Group>
              <Group>
                <Text size="sm" fw={500} w={160}>
                  Backend:
                </Text>
                <Badge color={backend === 'sql' ? 'blue' : 'orange'} variant="light">
                  {backend}
                </Badge>
              </Group>
              {backend === 'sql' ? (
                <>
                  <Group>
                    <Text size="sm" fw={500} w={160}>
                      Banco (db_identifier):
                    </Text>
                    <Text size="sm" ff="monospace">
                      {dbIdentifier}
                    </Text>
                  </Group>
                  <Group>
                    <Text size="sm" fw={500} w={160}>
                      Credencial (ID):
                    </Text>
                    <Text size="sm" ff="monospace">
                      {credentialId}
                    </Text>
                  </Group>
                  <Group align="flex-start">
                    <Text size="sm" fw={500} w={160}>
                      SQL:
                    </Text>
                    <Text size="sm" ff="monospace" style={{ whiteSpace: 'pre-wrap', flex: 1 }}>
                      {sql}
                    </Text>
                  </Group>
                </>
              ) : (
                <>
                  <Group>
                    <Text size="sm" fw={500} w={160}>
                      Instância (instance_id):
                    </Text>
                    <Text size="sm" ff="monospace">
                      {instanceId}
                    </Text>
                  </Group>
                  <Group>
                    <Text size="sm" fw={500} w={160}>
                      Região:
                    </Text>
                    <Text size="sm" ff="monospace">
                      {region}
                    </Text>
                  </Group>
                  <Group align="flex-start">
                    <Text size="sm" fw={500} w={160}>
                      Script:
                    </Text>
                    <Text size="sm" ff="monospace" style={{ whiteSpace: 'pre-wrap', flex: 1 }}>
                      {script}
                    </Text>
                  </Group>
                </>
              )}
              <Group>
                <Text size="sm" fw={500} w={160}>
                  Agendamento:
                </Text>
                <Text size="sm" ff="monospace">
                  {scheduleExpr}
                </Text>
              </Group>
              <Group>
                <Text size="sm" fw={500} w={160}>
                  Fuso Horário:
                </Text>
                <Text size="sm">{timezone}</Text>
              </Group>
              <Group>
                <Text size="sm" fw={500} w={160}>
                  Respeitar congelamento:
                </Text>
                <Text size="sm">{freezeRespect ? 'Sim' : 'Não'}</Text>
              </Group>
            </Stack>
          </Stack>
        )}
      </Paper>

      {/* ── Navegação ─────────────────────────────────────────────── */}
      <Group justify="space-between">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={14} />}
          onClick={handleBack}
          disabled={active === 0}
        >
          Anterior
        </Button>

        {active < steps.length - 1 ? (
          <Button rightSection={<IconArrowRight size={14} />} onClick={handleNext}>
            Próximo
          </Button>
        ) : (
          <Button
            leftSection={<IconSend size={14} />}
            onClick={handleSubmit}
            loading={createMutation.isPending}
            color="green"
          >
            Criar Rotina
          </Button>
        )}
      </Group>
    </Stack>
  )
}
